import { useCustomer } from "@/contexts/CustomerContext";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAddressAutocomplete } from "@/contexts/useAddressContext";
import UpsellProductList from "@/components/store/UpsellProductList";
import { useMerchantSlug } from "@/utils/hooks/useMerchantSlug";
import "@/assets/css/CartPage.css";
import { Branch } from "@/types/branch";
import { useLazyGetBranchFrontQuery } from "@/store/api/branchApi";
import { useFetchCustomerPointsQuery } from "@/store/api/userApi";
import PageLoader from "../ui/PageLoader";
import { PostPaidForm } from "./PostPaidForm";
import PickupWarningModal from "./PickupWarningModal";
import {
  GuestLoginModal,
  DeliveryFeeModal,
  DuplicatePhoneModal,
} from "./modals";
import {
  CartItemsView,
  DeliveryAddressForm,
  CheckoutDetailsView,
  PaymentFormView,
} from "./views";
import {
  useCartActions,
  useCartModals,
  useTipSelection,
  usePaymentForm,
  useDiscountCode,
  useDeliveryFee,
  useCartCalculations,
  useCheckoutFlow,
  useOrderSubmission,
} from "./hooks";

interface CartPageProps {
  handleCartClose?: () => void;
}

const CartPage: React.FC<CartPageProps> = ({ handleCartClose }) => {
  const hasServiceInCart = useSelector(
    (state: RootState) => state.cart.hasServiceInCart,
  );
  const { customer } = useCustomer();
  const merchantSlug = useMerchantSlug();
  const [branch, setBranch] = useState<Branch>();
  const [branchID, setBranchID] = useState<number>(0);
  const [usePoints, setUsePoints] = useState(false);
  const [comments, setComments] = useState<string>("None");
  const [branchLoading, setBranchLoading] = useState(false);

  // Address hooks
  const {
    address: deliveryAddress,
    setAddress: setDeliveryAddress,
    handlePlaceSelected,
    handleInputChange: handleDeliveryAddressChange,
  } = useAddressAutocomplete({
    street: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
  });

  const {
    address,
    setAddress,
    handlePlaceSelected: handleAddressPlaceSelected,
    handleInputChange: handleAddressChange,
  } = useAddressAutocomplete({
    street: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
  });

  // Custom hooks
  const { handleRemove, handleClearCart, handleQuantityChange } =
    useCartActions();

  const modals = useCartModals();

  const checkoutFlow = useCheckoutFlow({
    branch,
    hasServiceInCart,
    merchantSlug,
    handleCartClose,
  });

  const paymentForm = usePaymentForm({
    initialName: customer?.name || "",
    initialPhone: customer?.phone?.replace(/^\+1/, "") || "",
  });

  // Tip needs subtotal, but we need a preliminary calculation
  // We'll calculate subtotal first without tip dependencies
  const preliminaryCalc = useCartCalculations({
    branch,
    tipAmount: 0,
    discountValue: 0,
    deliveryFee: "",
    usePoints: false,
    points: "0",
  });

  const tip = useTipSelection({
    branch,
    subtotal: preliminaryCalc.subtotal,
  });

  const deliveryFeeHook = useDeliveryFee({
    branch,
    deliveryAddress,
    onSuccess: checkoutFlow.onDeliverySuccess,
  });

  // Points data
  const { data: pointsRes } = useFetchCustomerPointsQuery(
    {
      phone: `%2B${customer?.phone.substring(1) ?? ""}`,
      branch: branchID.toString(),
    },
    { skip: !customer || branchID === 0 },
  );
  const points = pointsRes?.points || customer?.wallet_points || "0.00";

  // Discount hook
  const discount = useDiscountCode({
    branchId: preliminaryCalc.branchId,
    orderItems: preliminaryCalc.orderItems,
    showCheckout: checkoutFlow.showCheckout,
  });

  // Full calculations with all values
  const calculations = useCartCalculations({
    branch,
    tipAmount: tip.tipAmount,
    discountValue: discount.discountValue,
    deliveryFee: deliveryFeeHook.deliveryFee,
    usePoints,
    points,
  });

  // Order submission
  const orderSubmission = useOrderSubmission({
    branch,
    branchId: calculations.branchId,
    merchantSlug,
    hasServiceInCart,
    adjustedCart: calculations.adjustedCart,
    orderItems: calculations.orderItems,
    orderMode: checkoutFlow.orderMode,
    state: checkoutFlow.state,
    token: paymentForm.token,
    cardData: paymentForm.cardData,
    address,
    phone: paymentForm.phone,
    comments,
    selectedPaymentMethod: paymentForm.selectedPaymentMethod,
    subtotal: calculations.subtotal,
    tax: calculations.tax,
    tipAmount: tip.tipAmount,
    total: calculations.total,
    serviceFee: calculations.serviceFee,
    deliveryFee: deliveryFeeHook.deliveryFee,
    deliveryFeeCalculated: deliveryFeeHook.deliveryFeeCalculated,
    deliveryAddressId: deliveryFeeHook.deliveryAddressId,
    usePoints,
    pointsToRedeem: calculations.pointsToRedeem,
    discountCode: discount.discountCode,
    discountValue: discount.discountValue,
    promotionId: discount.promotionId,
    promotionName: discount.promotionName,
    validateForm: paymentForm.validateForm,
    onDuplicatePhone: () => modals.updateIsOpen("duplicate", true),
  });

  // Combine loading states
  const loading =
    branchLoading || orderSubmission.loading || deliveryFeeHook.deliveryLoading;

  // Fetch branch data
  const [getBranchFront] = useLazyGetBranchFrontQuery();

  useEffect(() => {
    let isMounted = true;
    const fetchBranch = async () => {
      if (!merchantSlug) {
        if (isMounted) setBranchLoading(false);
        return;
      }
      if (isMounted) setBranchLoading(true);
      try {
        const url = `${window.location.hostname}${merchantSlug}`;
        const branchData = await getBranchFront(url).unwrap();
        if (isMounted) setBranch(branchData);
        if (isMounted) tip.initializeTip(branchData.tip_1 || 0);
        if (customer && customer.phone) {
          setBranchID(branchData.id || 0);
        }
      } catch (error) {
        console.error("Error fetching branch data:", error);
        if (isMounted) toast.error("Error fetching branch data");
      } finally {
        if (isMounted) setBranchLoading(false);
      }
    };
    fetchBranch();
    return () => {
      isMounted = false;
    };
  }, [merchantSlug, customer]);

  // Initialize customer data
  useEffect(() => {
    if (customer) {
      checkoutFlow.setCustomerState("customer");
      setAddress({
        street: customer.street ?? "",
        city: customer.city ?? "",
        province: customer.province ?? "",
        country: customer.country ?? "",
        postal_code: customer.postal_code ?? "",
      });
      setDeliveryAddress({
        street: customer.street ?? "",
        city: customer.city ?? "",
        province: customer.province ?? "",
        country: customer.country ?? "",
        postal_code: customer.postal_code ?? "",
      });
      const formattedPhone = customer.phone
        ? customer.phone.replace(/^\+1/, "")
        : "";
      paymentForm.setPhone(formattedPhone.trim());
    }
  }, [customer, setAddress, setDeliveryAddress]);

  const handleCheckoutButtonClick = () => {
    if (window.innerWidth > 768) {
      checkoutFlow.navigateToCart(() =>
        orderSubmission.handleCheckoutClick(
          () =>
            checkoutFlow.proceedToCheckout(() =>
              modals.updateIsOpen("guest", true),
            ),
          () => modals.openPickUpOnlyModal(),
        ),
      );
    } else {
      orderSubmission.handleCheckoutClick(
        () =>
          checkoutFlow.proceedToCheckout(() =>
            modals.updateIsOpen("guest", true),
          ),
        () => modals.openPickUpOnlyModal(),
      );
    }
  };

  const handlePayWithCard = () => {
    paymentForm.setSelectedPaymentMethod("Credit Card");
    if (
      checkoutFlow.orderMode === "Delivery" &&
      !deliveryFeeHook.deliveryFeeCalculated
    ) {
      toast.error("Delivery fee needs to be calculated first.");
      return;
    }
    checkoutFlow.goToPayment();
  };

  return (
    <PageLoader isFetching={loading}>
      <div className="w-full mx-auto md:px-2">
        <GuestLoginModal
          isOpen={modals.isOpen.guest}
          onClose={() => {
            modals.updateIsOpen("guest", false);
            checkoutFlow.setShowCartPage(true);
          }}
          onLogin={() =>
            checkoutFlow.goToLogin(() => modals.updateIsOpen("guest", false))
          }
          onContinueAsGuest={() =>
            checkoutFlow.handleGuestContinue(() =>
              modals.updateIsOpen("guest", false),
            )
          }
        />
        <DeliveryFeeModal
          isOpen={modals.isOpen.delivery}
          onClose={() => {
            deliveryFeeHook.setDeliveryFee("");
            modals.updateIsOpen("delivery", false);
          }}
          onConfirm={() => modals.updateIsOpen("delivery", false)}
          deliveryFee={deliveryFeeHook.deliveryFee}
        />
        <PickupWarningModal
          isOpen={modals.isPickUpOnlyModalOpen}
          onConfirm={() => {
            modals.closePickUpOnlyModal();
            checkoutFlow.proceedToCheckout(() =>
              modals.updateIsOpen("guest", true),
            );
          }}
          onCancel={() => {
            modals.closePickUpOnlyModal();
            checkoutFlow.setShowCartPage(true);
          }}
        />
        <DuplicatePhoneModal
          isOpen={modals.isOpen.duplicate}
          onClose={() => {}}
          onLogin={() =>
            checkoutFlow.goToLoginWithPhone(paymentForm.phone, () =>
              modals.updateIsOpen("duplicate", false),
            )
          }
          onUseNewNumber={() => {
            modals.updateIsOpen("duplicate", false);
            paymentForm.setPhone("");
          }}
          phone={paymentForm.phone}
        />

        {checkoutFlow.showCartPage && (
          <CartItemsView
            branch={branch}
            cart={calculations.cart}
            adjustedCart={calculations.adjustedCart}
            hasServiceInCart={hasServiceInCart}
            orderMode={checkoutFlow.orderMode}
            subtotal={calculations.subtotal}
            loading={loading}
            merchantSlug={merchantSlug}
            setOrderMode={checkoutFlow.setOrderMode}
            handleCartClose={handleCartClose}
            handleClearCart={handleClearCart}
            handleQuantityChange={handleQuantityChange}
            handleRemove={handleRemove}
            handleCheckoutButtonClick={handleCheckoutButtonClick}
            productPrice={calculations.productPrice}
            calculateItemTotal={calculations.calculateItemTotal}
          />
        )}

        {checkoutFlow.showPostPaidForm && (
          <PostPaidForm
            paymentPreference={
              branch?.payment_preference as "PostPaid" | "Customer's Choice"
            }
            setShowCheckout={checkoutFlow.setShowCheckout}
            setOrderMode={checkoutFlow.setOrderMode}
            setShowPostPaidForm={checkoutFlow.setShowPostPaidForm}
            submitPostPaidOrder={orderSubmission.submitPostPaidOrder}
            onCancel={() => {
              checkoutFlow.setShowPostPaidForm(false);
              checkoutFlow.setShowCartPage(true);
            }}
          />
        )}

        {checkoutFlow.showUpsellPage && branch?.upsell_feature && (
          <UpsellProductList
            onContinueCheckout={checkoutFlow.handleContinueCheckout}
            onContinueCart={() => {
              checkoutFlow.setShowCartPage(true);
              checkoutFlow.setShowUpsellPage(false);
            }}
          />
        )}

        {checkoutFlow.orderMode === "Delivery" &&
          checkoutFlow.showDeliveryForm && (
            <DeliveryAddressForm
              deliveryAddress={deliveryAddress}
              deliveryFeeError={deliveryFeeHook.deliveryFeeError}
              isDeliveryModalOpen={modals.isDeliveryModalOpen}
              onClose={() => {
                checkoutFlow.setShowDeliveryForm(false);
                checkoutFlow.setShowCartPage(true);
                deliveryFeeHook.resetDeliveryFee();
              }}
              onAddressChange={handleDeliveryAddressChange}
              onPlaceSelected={handlePlaceSelected}
              onSubmit={async (e) => {
                const result = await deliveryFeeHook.calculateDeliveryFee(e);
                if (result?.success) {
                  modals.updateIsOpen("delivery", true);
                }
              }}
              closeDeliveryModal={modals.closeDeliveryModal}
            />
          )}

        {checkoutFlow.showCheckout && (
          <CheckoutDetailsView
            branch={branch}
            adjustedCart={calculations.adjustedCart}
            hasServiceInCart={hasServiceInCart}
            orderMode={checkoutFlow.orderMode}
            deliveryAddress={deliveryAddress}
            deliveryFee={deliveryFeeHook.deliveryFee}
            deliveryFeeCalculated={deliveryFeeHook.deliveryFeeCalculated}
            tipOptions={tip.tipOptions}
            selectedTip={tip.selectedTip}
            customTip={tip.customTip}
            discountCode={discount.discountCode}
            discountValue={discount.discountValue}
            applyDiscountLoading={discount.applyDiscountLoading}
            subtotal={calculations.subtotal}
            tipAmount={tip.tipAmount}
            tax={calculations.tax}
            serviceFee={calculations.serviceFee}
            total={calculations.total}
            points={points}
            pointsToRedeem={calculations.pointsToRedeem}
            usePoints={usePoints}
            customer={customer}
            onClose={() => {
              checkoutFlow.resetToCart();
              setUsePoints(false);
              discount.resetDiscount();
            }}
            onEditDeliveryAddress={checkoutFlow.goToDeliveryForm}
            onTipSelection={tip.handleTipSelection}
            onCustomTipInput={tip.handleCustomTipInput}
            onDiscountCodeChange={discount.setDiscountCode}
            onApplyDiscount={discount.applyDiscount}
            onUsePointsChange={() => setUsePoints((p) => !p)}
            onCommentsChange={setComments}
            onPayWithCard={handlePayWithCard}
            onPayInStore={checkoutFlow.goToPostPaid}
            productPrice={calculations.productPrice}
          />
        )}

        {checkoutFlow.showPayment &&
          (checkoutFlow.orderMode === "Pick up" ||
            checkoutFlow.orderMode === "Appointment" ||
            (checkoutFlow.orderMode === "Delivery" &&
              deliveryFeeHook.deliveryFeeCalculated)) && (
            <PaymentFormView
              error={orderSubmission.error}
              loading={orderSubmission.loading}
              orderSuccess={orderSubmission.orderSuccess}
              cardData={paymentForm.cardData}
              errors={paymentForm.errors}
              phone={paymentForm.phone}
              address={address}
              iframeRef={paymentForm.iframeRef}
              tokenInputRef={paymentForm.tokenInputRef}
              isCVVModalOpen={modals.isCVVModalOpen}
              isBillingModalOpen={modals.isBillingModalOpen}
              onClose={() => {
                paymentForm.resetPaymentMethod();
                checkoutFlow.goToCheckoutFromPayment();
              }}
              onInputChange={paymentForm.handleInputChange}
              onAddressChange={handleAddressChange}
              onAddressPlaceSelected={handleAddressPlaceSelected}
              onPayNow={orderSubmission.handleCardPayment}
              openCVVModal={modals.openCVVModal}
              closeCVVModal={modals.closeCVVModal}
              openBillingModal={modals.openBillingModal}
              closeBillingModal={modals.closeBillingModal}
            />
          )}
      </div>
    </PageLoader>
  );
};

export default CartPage;
